import { memo, useState, useCallback } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { useTheme } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';
import { generatePath, useNavigate } from 'react-router-dom';

import AccountRow from '#client/components/AccountRow';
import ConfirmDeleteInstantFundingDebitCardModal from '#client/components/ConfirmDeleteInstantFundingDebitCardModal';
import MenuItemConnectedAccount from '#client/components/MenuItemConnectedAccount';
import InstantDepositModal from '#client/components/MoveMoney/InstantDepositModal';
import { TRY_ADD_CARD_AGAIN_STEP } from '#client/components/MoveMoney/InstantDepositModal/constants/instants';
import { ASTRA_STATUS } from '#client/constants/astra';
import useAstraProfileContext from '#client/hooks/useAstraProfileContext';
import useBankingPath from '#client/hooks/useBankingPath';
import useModalContext from '#client/hooks/useModalContext';

import { DebitCardWrapper, LabelStyled } from '../DebitCardsList.styles';

function DebitCard({
  accountType,
  cardHolderFirstName,
  cardHolderLastName,
  cardLastFourDigits,
  id,
  isLatest,
  loadingAccounts,
  usedAt,
  createBankingCardForInstantFunding,
  isDailyTransferLimitExceeded,
  isCardValidForTransfers,
  isCardExpired
}) {
  const {
    isNeedCreateDestinationCard,
    refreshAstraProfile,
    isEligibleForTransfers,
    accountStatus
  } = useAstraProfileContext();

  const [anchorEl, setAnchorEl] = useState(null);
  const { openModal } = useModalContext();
  const navigate = useNavigate();
  const bankingPath = useBankingPath();
  const [isOpenModal, setIsOpenModal] = useState(false);
  const theme = useTheme();
  const trackOverflowMenuOpenPressed = useButtonPressedCallback({
    elementName: 'overflow_menu_debit_card',
    elementText: 'overflow_menu',
    appPageName: 'active_banking_dashboard'
  });

  const handleOpen = (event) => {
    /**
     * Tracking Details: Tracking added to Add a card link on dashboard
     * Question: How many customers click on Add a card link?
     */
    event.stopPropagation();
    trackOverflowMenuOpenPressed();
    setIsOpenModal(true);
    setAnchorEl(event.currentTarget);
    event.currentTarget.blur();
  };

  const handleClose = useCallback((event) => {
    event?.stopPropagation();
    setIsOpenModal(false);
    setAnchorEl(null);
  }, []);

  const trackDepositPressed = useButtonPressedCallback({
    elementName: 'deposit_instant_from_dc',
    elementText: 'Deposit',
    appPageName: 'active_banking_dashboard'
  });

  const handleClickDepositButton = useCallback(
    async (event) => {
      trackDepositPressed();
      handleClose(event);

      if (isNeedCreateDestinationCard) {
        const { error: createCardError } = await createBankingCardForInstantFunding();
        if (createCardError) {
          openModal({
            component: InstantDepositModal,
            initStep: TRY_ADD_CARD_AGAIN_STEP,
            instantFundingAccount: {
              uuid: id
            }
          });

          return;
        }
        refreshAstraProfile();
      }

      openModal({
        component: InstantDepositModal,
        instantFundingAccount: {
          uuid: id
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, handleClose, openModal, isNeedCreateDestinationCard, refreshAstraProfile]
  );

  const trackDeletePressed = useButtonPressedCallback({
    elementName: 'delete_card_Instant_deposit',
    elementText: 'Delete',
    appPageName: 'active_banking_dashboard'
  });

  const handleClickDeleteButton = useCallback(
    async (event) => {
      trackDeletePressed();
      handleClose(event);
      openModal({
        component: ConfirmDeleteInstantFundingDebitCardModal,
        id,
        handleSuccess: refreshAstraProfile
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, handleClose, openModal, refreshAstraProfile]
  );

  const trackViewHistoryPressed = useButtonPressedCallback({
    elementName: 'txn_history_Instant_deposit',
    elementText: 'View history',
    appPageName: 'active_banking_dashboard'
  });
  const handleClickViewDebitCardHistory = useCallback(() => {
    trackViewHistoryPressed();
    navigate(
      generatePath(`${bankingPath}/debit-cards/:debitCardUuid`, {
        debitCardUuid: id
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getMenuItems = useCallback(() => {
    const viewHistoryOption = (
      <MenuItemConnectedAccount
        aria-label="item-action"
        key="view-debit-card-history"
        onClick={handleClickViewDebitCardHistory}
        sx={{
          color: `${theme.palette.text.tertiary}`
        }}
      >
        View history
      </MenuItemConnectedAccount>
    );
    const menuItemWhenUsedAtDashboard = [
      // Deposit option
      <MenuItemConnectedAccount
        aria-label="item-action"
        disabled={isDailyTransferLimitExceeded}
        key="deposit-money-item"
        onClick={handleClickDepositButton}
        sx={{
          color: `${theme.palette.text.tertiary}`
        }}
      >
        Deposit
      </MenuItemConnectedAccount>,
      // Delete option
      <MenuItemConnectedAccount
        aria-label="item-action"
        key="delete-astra-debit-card"
        onClick={handleClickDeleteButton}
        sx={{
          color: `${theme.palette.text.red}`
        }}
      >
        Delete
      </MenuItemConnectedAccount>
    ];

    /**
     * Remove the instant deposit option if astra status is suspended or isEligibleForTransfers is false
     */
    if (
      accountStatus === ASTRA_STATUS.SUSPENDED ||
      !isEligibleForTransfers ||
      !isCardValidForTransfers ||
      isCardExpired
    ) {
      menuItemWhenUsedAtDashboard.shift();
    }

    const lastItemIndex = menuItemWhenUsedAtDashboard.length - 1;
    menuItemWhenUsedAtDashboard.splice(lastItemIndex, 0, viewHistoryOption);

    return menuItemWhenUsedAtDashboard;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    id,
    accountStatus,
    isCardExpired,
    isCardValidForTransfers,
    isEligibleForTransfers,
    handleClickDepositButton,
    handleClickDeleteButton,
    isDailyTransferLimitExceeded
  ]);

  return (
    <DebitCardWrapper
      aria-label="debit card item"
      isLatest={isLatest}
      usedAtDashboard={DebitCardWrapper}
    >
      <AccountRow
        accountNumber={cardLastFourDigits}
        accountType={usedAt === 'dashboard' ? accountType : null}
        anchorEl={anchorEl}
        clickOnItemHandler={handleClickViewDebitCardHistory}
        handleClose={handleClose}
        handleOpen={handleOpen}
        id={id}
        isOpenModal={isOpenModal}
        label={
          isCardExpired || !isCardValidForTransfers ? (
            <LabelStyled
              aria-label="label"
              sx={{
                backgroundColor: theme.palette.background.red.light
              }}
            >
              <Text
                color="primary"
                size="xs"
                textAlign="center"
                whiteSpace="nowrap"
              >
                {isCardExpired ? 'Expired' : 'Type not accepted'}
              </Text>
            </LabelStyled>
          ) : null
        }
        loading={loadingAccounts}
        menuItems={getMenuItems()}
        name={`${cardHolderFirstName} ${cardHolderLastName}`}
        usedAt={usedAt}
      />
    </DebitCardWrapper>
  );
}

DebitCard.propTypes = {
  accountType: PropTypes.string,
  cardHolderFirstName: PropTypes.string.isRequired,
  cardHolderLastName: PropTypes.string.isRequired,
  cardLastFourDigits: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  isLatest: PropTypes.bool,
  loadingAccounts: PropTypes.bool,
  usedAt: PropTypes.string,
  createBankingCardForInstantFunding: PropTypes.func,
  isCardValidForTransfers: PropTypes.bool,
  isDailyTransferLimitExceeded: PropTypes.bool,
  isCardExpired: PropTypes.bool
};
export default memo(DebitCard);
